import React, { useEffect, useMemo, useState } from 'react';
import { apiDelete, apiGet, apiPost, apiPut } from '../utils/api';

type BracketType = 'SINGLE_ELIMINATION' | 'ROUND_ROBIN' | 'MIXED';

interface PointsSystemStageRule {
  id: string;
  schemeId: string;
  stageCode: string;
  stageName: string;
  sortOrder: number;
  note?: string | null;
  isActive: boolean;
  winPoints: number;
  stagePoints: number;
}

interface PointsSystemScheme {
  id: string;
  name: string;
  bracketTemplateId?: string | null;
  bracketType: BracketType;
  ttlWeeks: number;
  isActive: boolean;
  stages: PointsSystemStageRule[];
  bracketTemplate?: {
    id: string;
    name: string;
    format: string;
    playerCount: number;
  } | null;
}

interface BracketTemplateOption {
  id: string;
  name: string;
  format: string;
  playerCount: number;
}

const BRACKET_TYPE_LABELS: Record<string, string> = {
  SINGLE_ELIMINATION: 'Плей-офф',
  ROUND_ROBIN: 'Групповой этап',
  MIXED: 'Смешанный (группы + плей-офф)'
};

const STAGE_PRESETS_BY_BRACKET: Record<BracketType, Array<{ code: string; name: string; order: number }>> = {
  ROUND_ROBIN: [
    { code: 'GROUP_1', name: 'Групповой - 1', order: 1 },
    { code: 'GROUP_2', name: 'Групповой - 2', order: 2 },
    { code: 'GROUP_3', name: 'Групповой - 3', order: 3 },
    { code: 'GROUP_4', name: 'Групповой - 4', order: 4 },
    { code: 'GROUP_PLACE_1', name: 'Место в группе - 1', order: 101 },
    { code: 'GROUP_PLACE_2', name: 'Место в группе - 2', order: 102 },
    { code: 'GROUP_PLACE_3', name: 'Место в группе - 3', order: 103 },
    { code: 'GROUP_PLACE_4', name: 'Место в группе - 4', order: 104 },
    { code: 'GROUP_PLACE_5', name: 'Место в группе - 5', order: 105 },
    { code: 'GROUP_PLACE_6', name: 'Место в группе - 6', order: 106 },
    { code: 'GROUP_PLACE_7', name: 'Место в группе - 7', order: 107 },
    { code: 'GROUP_PLACE_8', name: 'Место в группе - 8', order: 108 }
  ],
  SINGLE_ELIMINATION: [
    { code: 'ROUND_OF_32', name: '1/16 финала', order: 1 },
    { code: 'ROUND_OF_16', name: '1/8 финала', order: 2 },
    { code: 'QUARTERFINAL', name: 'Четвертьфинал', order: 3 },
    { code: 'SEMIFINAL', name: 'Полуфинал', order: 4 },
    { code: 'FINAL', name: 'Финал', order: 5 }
  ],
  MIXED: [
    { code: 'GROUP_1', name: 'Групповой - 1', order: 1 },
    { code: 'GROUP_2', name: 'Групповой - 2', order: 2 },
    { code: 'GROUP_3', name: 'Групповой - 3', order: 3 },
    { code: 'GROUP_PLACE_1', name: 'Место в группе - 1', order: 101 },
    { code: 'GROUP_PLACE_2', name: 'Место в группе - 2', order: 102 },
    { code: 'GROUP_PLACE_3', name: 'Место в группе - 3', order: 103 },
    { code: 'GROUP_PLACE_4', name: 'Место в группе - 4', order: 104 },
    { code: 'GROUP_PLACE_5', name: 'Место в группе - 5', order: 105 },
    { code: 'GROUP_PLACE_6', name: 'Место в группе - 6', order: 106 },
    { code: 'GROUP_PLACE_7', name: 'Место в группе - 7', order: 107 },
    { code: 'GROUP_PLACE_8', name: 'Место в группе - 8', order: 108 },
    { code: 'ROUND_OF_16', name: '1/8 финала', order: 4 },
    { code: 'QUARTERFINAL', name: 'Четвертьфинал', order: 5 },
    { code: 'SEMIFINAL', name: 'Полуфинал', order: 6 },
    { code: 'FINAL', name: 'Финал', order: 7 }
  ]
};

const mapLegacyStageCodeToPreset = (stageCode: string) => {
  if (stageCode === 'ROUND_OF_8') return 'QUARTERFINAL';
  return stageCode;
};

const emptySchemeForm = {
  name: '',
  bracketTemplateId: '',
  ttlWeeks: 52,
  isActive: true,
  initDefaults: true
};

const emptyStageForm = {
  stageCode: '',
  stageName: '',
  sortOrder: 1,
  winPoints: 0,
  stagePoints: 0,
  note: '',
  isActive: true
};

const PointsSystem: React.FC = () => {
  const [schemes, setSchemes] = useState<PointsSystemScheme[]>([]);
  const [bracketTemplates, setBracketTemplates] = useState<BracketTemplateOption[]>([]);
  const [selectedSchemeId, setSelectedSchemeId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [isSchemeModalOpen, setIsSchemeModalOpen] = useState(false);
  const [isStageModalOpen, setIsStageModalOpen] = useState(false);
  const [editingScheme, setEditingScheme] = useState<PointsSystemScheme | null>(null);
  const [editingStage, setEditingStage] = useState<PointsSystemStageRule | null>(null);
  const [schemeFormData, setSchemeFormData] = useState(emptySchemeForm);
  const [stageFormData, setStageFormData] = useState(emptyStageForm);

  const selectedScheme = useMemo(
    () => schemes.find((s) => s.id === selectedSchemeId) || null,
    [schemes, selectedSchemeId]
  );

  const sortedStages = useMemo(
    () => [...(selectedScheme?.stages || [])].sort((a, b) => a.sortOrder - b.sortOrder),
    [selectedScheme]
  );

  const stagePresets = useMemo(() => {
    if (!selectedScheme) return [];
    return STAGE_PRESETS_BY_BRACKET[selectedScheme.bracketType] || [];
  }, [selectedScheme]);

  const fetchSchemes = async () => {
    try {
      setLoading(true);
      const [schemesData, templatesData] = await Promise.all([
        apiGet<PointsSystemScheme[]>('/points-system'),
        apiGet<BracketTemplateOption[]>('/bracket-templates')
      ]);
      const data = schemesData;
      const nextSchemes = Array.isArray(data) ? data : [];
      setSchemes(nextSchemes);
      setBracketTemplates(Array.isArray(templatesData) ? templatesData : []);
      if (!selectedSchemeId && nextSchemes.length > 0) {
        setSelectedSchemeId(nextSchemes[0].id);
      } else if (selectedSchemeId && !nextSchemes.some((s) => s.id === selectedSchemeId)) {
        setSelectedSchemeId(nextSchemes[0]?.id || '');
      }
    } catch (error) {
      console.error('Ошибка загрузки схем начисления очков:', error);
      setSchemes([]);
      setSelectedSchemeId('');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSchemes();
  }, []);

  const openCreateSchemeModal = () => {
    setEditingScheme(null);
    setSchemeFormData({
      ...emptySchemeForm,
      bracketTemplateId: bracketTemplates[0]?.id || ''
    });
    setIsSchemeModalOpen(true);
  };

  const openEditSchemeModal = (scheme: PointsSystemScheme) => {
    setEditingScheme(scheme);
    setSchemeFormData({
      name: scheme.name,
      bracketTemplateId: scheme.bracketTemplateId || '',
      ttlWeeks: scheme.ttlWeeks || 52,
      isActive: scheme.isActive,
      initDefaults: false
    });
    setIsSchemeModalOpen(true);
  };

  const openCreateStageModal = () => {
    if (!selectedScheme) return;
    setEditingStage(null);
    const nextOrder = (sortedStages.reduce((max, s) => Math.max(max, s.sortOrder), 0) || 0) + 1;
    setStageFormData({
      ...emptyStageForm,
      sortOrder: nextOrder
    });
    setIsStageModalOpen(true);
  };

  const openEditStageModal = (stage: PointsSystemStageRule) => {
    setEditingStage(stage);
    setStageFormData({
      stageCode: stage.stageCode,
      stageName: stage.stageName,
      sortOrder: stage.sortOrder,
      winPoints: stage.winPoints,
      stagePoints: stage.stagePoints || 0,
      note: stage.note || '',
      isActive: stage.isActive
    });
    setIsStageModalOpen(true);
  };

  const handleSaveScheme = async () => {
    try {
      if (!schemeFormData.name.trim()) {
        alert('Введите название схемы');
        return;
      }
      if (!schemeFormData.bracketTemplateId) {
        alert('Выберите шаблон сетки');
        return;
      }
      if (!Number.isFinite(Number(schemeFormData.ttlWeeks)) || Number(schemeFormData.ttlWeeks) < 1) {
        alert('Срок жизни очков должен быть не меньше 1 недели');
        return;
      }

      if (editingScheme) {
        await apiPut(`/points-system/schemes/${editingScheme.id}`, {
          name: schemeFormData.name.trim(),
          bracketTemplateId: schemeFormData.bracketTemplateId,
          ttlWeeks: Math.trunc(Number(schemeFormData.ttlWeeks)),
          isActive: schemeFormData.isActive
        });
      } else {
        const created = await apiPost<PointsSystemScheme>('/points-system/schemes', {
          name: schemeFormData.name.trim(),
          bracketTemplateId: schemeFormData.bracketTemplateId,
          ttlWeeks: Math.trunc(Number(schemeFormData.ttlWeeks)),
          isActive: schemeFormData.isActive,
          initDefaults: schemeFormData.initDefaults
        });
        if (created?.id) {
          setSelectedSchemeId(created.id);
        }
      }

      setIsSchemeModalOpen(false);
      await fetchSchemes();
    } catch (error: any) {
      alert(`Ошибка сохранения: ${error?.message || 'Неизвестная ошибка'}`);
    }
  };

  const handleDeleteScheme = async (id: string) => {
    if (!confirm('Удалить схему начисления очков и все ее этапы?')) return;
    try {
      await apiDelete(`/points-system/schemes/${id}`);
      await fetchSchemes();
    } catch (error: any) {
      alert(`Ошибка удаления: ${error?.message || 'Неизвестная ошибка'}`);
    }
  };

  const handleSaveStage = async () => {
    if (!selectedScheme) return;
    try {
      if (!stageFormData.stageCode.trim()) {
        alert('Укажите код этапа');
        return;
      }
      if (!stageFormData.stageName.trim()) {
        alert('Укажите название этапа');
        return;
      }

      if (editingStage) {
        await apiPut(`/points-system/stages/${editingStage.id}`, {
          stageCode: stageFormData.stageCode.trim().toUpperCase(),
          stageName: stageFormData.stageName.trim(),
          sortOrder: stageFormData.sortOrder,
          winPoints: stageFormData.winPoints,
          stagePoints: stageFormData.stagePoints,
          note: stageFormData.note.trim() || null,
          isActive: stageFormData.isActive
        });
      } else {
        await apiPost(`/points-system/schemes/${selectedScheme.id}/stages`, {
          stageCode: stageFormData.stageCode.trim().toUpperCase(),
          stageName: stageFormData.stageName.trim(),
          sortOrder: stageFormData.sortOrder,
          winPoints: stageFormData.winPoints,
          stagePoints: stageFormData.stagePoints,
          note: stageFormData.note.trim() || null,
          isActive: stageFormData.isActive
        });
      }

      setIsStageModalOpen(false);
      await fetchSchemes();
    } catch (error: any) {
      alert(`Ошибка сохранения этапа: ${error?.message || 'Неизвестная ошибка'}`);
    }
  };

  const handleDeleteStage = async (id: string) => {
    if (!confirm('Удалить этап?')) return;
    try {
      await apiDelete(`/points-system/stages/${id}`);
      await fetchSchemes();
    } catch (error: any) {
      alert(`Ошибка удаления этапа: ${error?.message || 'Неизвестная ошибка'}`);
    }
  };

  const handlePresetChange = (presetCode: string) => {
    const preset = stagePresets.find((p) => p.code === presetCode);
    if (!preset) {
      setStageFormData({ ...stageFormData, stageCode: presetCode });
      return;
    }
    setStageFormData({
      ...stageFormData,
      stageCode: preset.code,
      stageName: preset.name,
      sortOrder: preset.order
    });
  };

  return (
    <div className="py-6">
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Система начисления очков</h1>
        <p className="text-sm text-gray-600">
          Создавайте несколько схем начисления, выбирайте тип сетки и настраивайте этапы с порядком и очками.
        </p>
      </div>

      {loading ? (
        <div className="bg-white rounded-lg shadow-md p-8 text-center text-gray-500">Загрузка...</div>
      ) : (
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex flex-col lg:flex-row gap-3 lg:items-center lg:justify-between">
              <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
                <label className="text-sm text-gray-600">Схема:</label>
                <select
                  value={selectedSchemeId}
                  onChange={(e) => setSelectedSchemeId(e.target.value)}
                  className="border border-gray-300 rounded-md px-3 py-2 min-w-[280px]"
                >
                  {schemes.length === 0 ? (
                    <option value="">Нет схем</option>
                  ) : (
                    schemes.map((scheme) => (
                      <option key={scheme.id} value={scheme.id}>
                        {scheme.name} ({scheme.bracketTemplate?.name || BRACKET_TYPE_LABELS[scheme.bracketType]})
                      </option>
                    ))
                  )}
                </select>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={openCreateSchemeModal}
                  className="bg-blue-600 text-white px-3 py-2 rounded-md text-sm hover:bg-blue-700"
                >
                  + Новая схема
                </button>
                {selectedScheme && (
                  <>
                    <button
                      onClick={() => openEditSchemeModal(selectedScheme)}
                      className="px-3 py-2 rounded-md text-sm border border-gray-300 hover:bg-gray-50"
                    >
                      Изменить схему
                    </button>
                    <button
                      onClick={() => handleDeleteScheme(selectedScheme.id)}
                      className="px-3 py-2 rounded-md text-sm border border-red-300 text-red-700 hover:bg-red-50"
                    >
                      Удалить схему
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>

          {selectedScheme && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">{selectedScheme.name}</h2>
                  <p className="text-sm text-gray-600">
                    Тип сетки: {selectedScheme.bracketTemplate?.name || BRACKET_TYPE_LABELS[selectedScheme.bracketType]}
                  </p>
                  <p className="text-sm text-gray-600">
                    Срок жизни очков: {selectedScheme.ttlWeeks || 52} нед.
                  </p>
                </div>
                <button
                  onClick={openCreateStageModal}
                  className="bg-blue-600 text-white px-3 py-1.5 rounded-md text-sm hover:bg-blue-700"
                >
                  + Добавить этап
                </button>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm border border-gray-200">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="text-left px-4 py-2 border-b border-gray-200">Порядок</th>
                      <th className="text-left px-4 py-2 border-b border-gray-200">Код этапа</th>
                      <th className="text-left px-4 py-2 border-b border-gray-200">Этап</th>
                      <th className="text-left px-4 py-2 border-b border-gray-200">Очки за победу</th>
                      <th className="text-left px-4 py-2 border-b border-gray-200">Очки за этап</th>
                      <th className="text-left px-4 py-2 border-b border-gray-200">Примечание</th>
                      <th className="text-right px-4 py-2 border-b border-gray-200">Действия</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedStages.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="px-4 py-6 text-center text-gray-500">
                          В этой схеме пока нет этапов
                        </td>
                      </tr>
                    ) : (
                      sortedStages.map((stage) => (
                        <tr key={stage.id} className="hover:bg-gray-50">
                          <td className="px-4 py-2 border-b border-gray-100">{stage.sortOrder}</td>
                          <td className="px-4 py-2 border-b border-gray-100">{stage.stageCode}</td>
                          <td className="px-4 py-2 border-b border-gray-100">{stage.stageName}</td>
                          <td className="px-4 py-2 border-b border-gray-100 font-semibold">{stage.winPoints}</td>
                          <td className="px-4 py-2 border-b border-gray-100 font-semibold">{stage.stagePoints || 0}</td>
                          <td className="px-4 py-2 border-b border-gray-100 text-gray-600">{stage.note || '—'}</td>
                          <td className="px-4 py-2 border-b border-gray-100 text-right">
                            <button
                              onClick={() => openEditStageModal(stage)}
                              className="text-blue-600 hover:text-blue-800 mr-3"
                            >
                              Изменить
                            </button>
                            <button
                              onClick={() => handleDeleteStage(stage.id)}
                              className="text-red-600 hover:text-red-800"
                            >
                              Удалить
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {isSchemeModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg mx-4">
            <h3 className="text-xl font-bold mb-4">
              {editingScheme ? 'Редактировать схему' : 'Создать схему'}
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Название схемы</label>
                <input
                  type="text"
                  value={schemeFormData.name}
                  onChange={(e) => setSchemeFormData({ ...schemeFormData, name: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Тип сетки</label>
                <select
                  value={schemeFormData.bracketTemplateId}
                  onChange={(e) =>
                    setSchemeFormData({ ...schemeFormData, bracketTemplateId: e.target.value })
                  }
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                >
                  {bracketTemplates.length === 0 ? (
                    <option value="">Нет шаблонов сеток</option>
                  ) : (
                    bracketTemplates.map((tpl) => (
                      <option key={tpl.id} value={tpl.id}>
                        {tpl.name} ({BRACKET_TYPE_LABELS[tpl.format] || tpl.format}, {tpl.playerCount})
                      </option>
                    ))
                  )}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Срок жизни очков (недели)</label>
                <input
                  type="number"
                  min={1}
                  value={schemeFormData.ttlWeeks}
                  onChange={(e) =>
                    setSchemeFormData({
                      ...schemeFormData,
                      ttlWeeks: Math.max(1, Number(e.target.value) || 1)
                    })
                  }
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                />
                <p className="text-xs text-gray-500 mt-1">
                  ATP-принцип: обычно 52 недели.
                </p>
              </div>

              {!editingScheme && (
                <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={schemeFormData.initDefaults}
                    onChange={(e) => setSchemeFormData({ ...schemeFormData, initDefaults: e.target.checked })}
                  />
                  Добавить типовой набор этапов автоматически
                </label>
              )}
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => setIsSchemeModalOpen(false)}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Отмена
              </button>
              <button
                onClick={handleSaveScheme}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Сохранить
              </button>
            </div>
          </div>
        </div>
      )}

      {isStageModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-xl mx-4">
            <h3 className="text-xl font-bold mb-4">
              {editingStage ? 'Редактировать этап' : 'Создать этап'}
            </h3>

            <div className="space-y-4">
              {selectedScheme && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Шаблон этапа</label>
                  <select
                    value={
                      stagePresets.some((p) => p.code === mapLegacyStageCodeToPreset(stageFormData.stageCode))
                        ? mapLegacyStageCodeToPreset(stageFormData.stageCode)
                        : ''
                    }
                    onChange={(e) => handlePresetChange(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                  >
                    <option value="">Выбрать из типа сетки ({BRACKET_TYPE_LABELS[selectedScheme.bracketType]})</option>
                    {stagePresets.map((preset) => (
                      <option key={preset.code} value={preset.code}>
                        {preset.order}. {preset.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Код этапа</label>
                  <input
                    type="text"
                    value={stageFormData.stageCode}
                    onChange={(e) =>
                      setStageFormData({ ...stageFormData, stageCode: e.target.value.toUpperCase().replace(/\s+/g, '_') })
                    }
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    placeholder="ROUND_OF_16"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Для очков за место в группе используйте коды вида GROUP_PLACE_1, GROUP_PLACE_2 и т.д.
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Порядок</label>
                  <input
                    type="number"
                    value={stageFormData.sortOrder}
                    onChange={(e) => setStageFormData({ ...stageFormData, sortOrder: Number(e.target.value) || 0 })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Название этапа</label>
                <input
                  type="text"
                  value={stageFormData.stageName}
                  onChange={(e) => setStageFormData({ ...stageFormData, stageName: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Очки за победу</label>
                  <input
                    type="number"
                    value={stageFormData.winPoints}
                    onChange={(e) => setStageFormData({ ...stageFormData, winPoints: Number(e.target.value) || 0 })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Очки за этап</label>
                  <input
                    type="number"
                    value={stageFormData.stagePoints}
                    onChange={(e) => setStageFormData({ ...stageFormData, stagePoints: Number(e.target.value) || 0 })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Очки за поражение на данном этапе.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Примечание</label>
                  <input
                    type="text"
                    value={stageFormData.note}
                    onChange={(e) => setStageFormData({ ...stageFormData, note: e.target.value })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                  />
                </div>
                <div />
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => setIsStageModalOpen(false)}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Отмена
              </button>
              <button
                onClick={handleSaveStage}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Сохранить
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PointsSystem;
